import { useTranslation } from "react-i18next";

const Row = ({ label, value, tone = "", testId, strong }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <span className={`text-sm ${strong ? "font-bold" : "text-muted-foreground"}`}>{label}</span>
    <span className={`font-mono text-sm font-bold ${tone}`} data-testid={testId}>
      {value}
    </span>
  </div>
);

export const PayslipLines = ({ slip }) => {
  const { t } = useTranslation();
  const cur = slip.currency;
  const a = slip.allowances;

  const earnings = [
    ["basic", slip.basic_salary, "payslip-basic", true],
    ["housing", a.housing, "payslip-housing"],
    ["transport", a.transport, "payslip-transport"],
    ["otherAllowance", a.other, "payslip-other-allowance"],
    ["bonuses", slip.bonuses, "payslip-bonuses"],
  ].filter(([, value], index) => index === 0 || value);

  const deductions = [
    ["absence", slip.absence_deduction, "payslip-absence", { count: slip.absent_days }],
    ["late", slip.late_deduction, "payslip-late", { count: slip.late_minutes }],
    ["unpaidLeave", slip.unpaid_leave_deduction, "payslip-unpaid", { count: slip.unpaid_leave_days }],
    ["advanceDeduction", slip.advance_deduction, "payslip-advance"],
    ["otherDeductions", slip.other_deductions, "payslip-other-deductions"],
  ].filter(([, value]) => value);

  return (
    <div className="mt-4 grid gap-4 sm:grid-cols-2">
      <div className="rounded-2xl p-4 shadow-neu-in" data-testid="payslip-earnings">
        <p className="label-xs">{t("payslip.earnings")}</p>
        <div className="mt-2 divide-y divide-border/50">
          {earnings.map(([key, value, testId]) => (
            <Row key={key} label={t(`payslip.${key}`)} value={`${value} ${cur}`} testId={testId} />
          ))}
          <Row label={t("payslip.gross")} value={`${slip.gross_pay} ${cur}`} testId="payslip-gross" strong tone="text-primary" />
        </div>
      </div>

      <div className="rounded-2xl p-4 shadow-neu-in" data-testid="payslip-deductions">
        <p className="label-xs">{t("payslip.deductions")}</p>
        <div className="mt-2 divide-y divide-border/50">
          {!deductions.length ? (
            <p className="py-2 text-sm text-muted-foreground" data-testid="payslip-no-deductions">
              {t("payslip.none")}
            </p>
          ) : (
            deductions.map(([key, value, testId, params]) => (
              <Row key={key} label={t(`payslip.${key}`, params)} value={`- ${value} ${cur}`} tone="text-destructive" testId={testId} />
            ))
          )}
          <Row
            label={t("payslip.totalDeductions")}
            value={`- ${slip.total_deductions} ${cur}`}
            tone="text-destructive"
            testId="payslip-total-deductions"
            strong
          />
        </div>
      </div>

      <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-primary/10 p-4">
        <span className="text-sm font-black">{t("payslip.net")}</span>
        <span className="font-mono text-2xl font-black text-primary" data-testid="payslip-net">
          {slip.net_pay} {cur}
        </span>
      </div>

      {slip.advance_installments.length ? (
        <ul className="sm:col-span-2 space-y-1" data-testid="payslip-installments">
          {slip.advance_installments.map((item) => (
            <li key={item.id} className="flex justify-between font-mono text-xs text-muted-foreground">
              <span>{t("payslip.installmentOf", { month: item.month })}</span>
              <span>
                {item.amount} {item.currency}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="sm:col-span-2 text-xs text-muted-foreground" data-testid="payslip-period">
        {t("payslip.payrollDay", { day: slip.payroll_day })}
        {" · "}
        {t("payslip.period", { start: slip.period_start, end: slip.period_end })}
        {" · "}
        {t("payslip.dayOf", { day: slip.current_day, total: slip.days_in_month })}
      </p>
    </div>
  );
};
